/**
 * The Snapshot — the single interface between agent, worker and web.
 *
 * SIGN CONVENTION (CLAUDE.md §5): negative = debit, positive = credit, matching
 * Tally's raw XML. Normalised ONCE in the agent's normalise.ts. Never flip signs
 * anywhere else, especially not in the web layer.
 *
 * Dates are `YYYY-MM-DD`. Timestamps are ISO 8601 UTC.
 */

/** Bump on any breaking change to this shape. */
export const SNAPSHOT_SCHEMA = 1;
export type SnapshotSchemaVersion = typeof SNAPSHOT_SCHEMA;

export type Snapshot = {
  schema: SnapshotSchemaVersion;
  /** slug, e.g. "acme" */
  companyId: string;
  /** ISO 8601 UTC */
  generatedAt: string;
  lastAlterIdMaster: number;
  lastAlterIdVoucher: number;

  /**
   * Set only on a REDACTED copy published for a role.
   *
   * Absent means the full snapshot. The app uses it to label what is on screen
   * and to hide sections that were withheld — a Sales user seeing an empty
   * Balance Sheet should be told why, not left wondering whether the sync
   * broke. It is a LABEL, never a permission: the restriction is that the data
   * is not in the file at all.
   */
  role?: { id: string; label: string };

  company: Company;

  groups: Group[];
  ledgers: Ledger[];
  voucherTypes: VoucherType[];
  costCategories: CostCategory[];
  costCentres: CostCentre[];
  units: Unit[];
  godowns: Godown[];
  stockGroups: StockGroup[];
  stockItems: StockItem[];
  vouchers: Voucher[];
};

export type Company = {
  name: string;
  /** YYYY-MM-DD */
  financialYearFrom: string;
  booksFrom: string;
  baseCurrency: string;
  gstin?: string;
  state?: string;
};

export type Group = {
  id: string;
  name: string;
  parent: string | null;
  /** top-level ancestor */
  primaryGroup: string;
  /** P&L vs Balance Sheet */
  isRevenue: boolean;
  /** debit-nature */
  isDeemedPositive: boolean;
  affectsGrossProfit: boolean;
};

export type Ledger = {
  id: string;
  name: string;
  /** group name */
  parent: string;
  /** +ve = debit */
  openingBalance: number;
  closingBalance: number;
  isBillWiseOn: boolean;
  isCostCentresOn: boolean;
  creditPeriodDays?: number;
  contact?: LedgerContact;
  address?: string[];
  state?: string;
  pincode?: string;
  gstin?: string;
  gstRegistrationType?: string;
};

export type LedgerContact = {
  phone?: string;
  mobile?: string;
  email?: string;
};

/**
 * NOTE: the shapes below are NOT specified in CLAUDE.md §5 — these are minimal
 * conventional fields, to be confirmed against real TDL output in Phase 2 and
 * recorded in docs/tally-fields.md before any of them is relied upon.
 */
export type VoucherType = {
  id: string;
  name: string;
  /** the built-in type this is derived from, e.g. "Sales" */
  parent: string;
  affectsStock: boolean;
  isDeemedPositive: boolean;
  numberingMethod?: string;
};

export type CostCategory = {
  id: string;
  name: string;
  allocateRevenue: boolean;
  allocateNonRevenue: boolean;
};

export type CostCentre = {
  id: string;
  name: string;
  parent: string | null;
  category: string;
};

export type Unit = {
  id: string;
  name: string;
  isSimple: boolean;
  decimalPlaces: number;
  /** compound units only */
  baseUnit?: string;
  additionalUnit?: string;
  conversion?: number;
};

export type Godown = {
  id: string;
  name: string;
  parent: string | null;
};

export type StockGroup = {
  id: string;
  name: string;
  parent: string | null;
};

export type StockItem = {
  id: string;
  name: string;
  parent: string;
  category?: string;
  baseUnit: string;
  openingQty: number;
  openingValue: number;
  closingQty: number;
  closingValue: number;
  hsnCode?: string;
  gstRate?: number;
};

export type Voucher = {
  /** GUID — stable across syncs */
  id: string;
  /** YYYY-MM-DD */
  date: string;
  voucherType: string;
  voucherNumber: string;
  reference?: string;
  referenceDate?: string;
  narration?: string;
  partyLedger?: string;
  isCancelled: boolean;
  isOptional: boolean;
  ledgerEntries: LedgerEntry[];
  inventoryEntries: InventoryEntry[];
};

export type LedgerEntry = {
  ledger: string;
  /** -ve = debit, matching Tally's convention */
  amount: number;
  isDeemedPositive: boolean;
  billAllocations: BillAllocation[];
  costAllocations: CostAllocation[];
  bankAllocation?: BankAllocation;
};

export type BillType = "New Ref" | "Agst Ref" | "Advance" | "On Account";

export type BillAllocation = {
  billName: string;
  billType: BillType;
  amount: number;
  /** drives ageing */
  dueDate?: string;
};

export type InventoryEntry = {
  stockItem: string;
  qty: number;
  rate: number;
  amount: number;
  unit: string;
  godown?: string;
  batch?: string;
  expiryDate?: string;
  ledgerAllocations: InventoryLedgerAllocation[];
};

export type InventoryLedgerAllocation = {
  ledger: string;
  amount: number;
};

export type CostAllocation = {
  costCentre: string;
  category?: string;
  amount: number;
};

export type BankAllocation = {
  instrumentNumber?: string;
  instrumentDate?: string;
  /** absent ⇒ unreconciled */
  bankDate?: string;
  transactionType?: string;
};

/** What GET /v1/meta/:companyId returns, and what KV stores as value metadata. */
export type SnapshotMeta = {
  /** SHA-256 hex of the plaintext JSON bytes (NOT of the ciphertext) */
  hash: string;
  generatedAt: string;
  sizeBytes: number;
  schema: number;
};

export type CompanyMeta = SnapshotMeta & { companyId: string };
